import React, {useMemo} from "react";
import {styled} from "@mui/material/styles";
import {useMenuTranslation} from "./menuTranslationContext";
import {useIsAppAdmin} from "../auths/useIsAppAdmin";
import {useIsAdminOfAtleastOneMuseum} from "../auths/useIsAdminOfAtleastOneMuseum";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import RestoreIcon from "@mui/icons-material/Restore";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import HomeIcon from "@mui/icons-material/Home";
import AssessmentIcon from '@mui/icons-material/Assessment';
import {MENU_CLOSED, useAppDispatch, useAppState} from "../app/AppContext";
import {useNavigate} from "react-router";
import {useProjectState} from "../project/projectContext";
import {useHasArcheologyModule} from "../auths/useHasArcheologyModule";
import {useAuthsState} from "../auths/authsContext";
import {ListItemButton} from "@mui/material";
import DataObjectIcon from '@mui/icons-material/DataObject';
import DatasetIcon from '@mui/icons-material/Dataset';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ListIcon from '@mui/icons-material/List';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import {CLEAR_FQ, CLEAR_ONLY_MINE, SET_ONLY_MINE, useSearchDispatch} from "../search/SearchContext";
import InfoIcon from "@mui/icons-material/Info";
import {OPEN_ABOUT_DIALOG, useAboutDispatch} from "../about/AboutContext";
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import {InstallAppMenuItem} from "../install-app/InstallAppMenuItem";

const PREFIX = "Menu";

const classes = {
    drawer: `${PREFIX}-drawer`,
    drawerPaper: `${PREFIX}-drawerPaper`,
    drawerHeader: `${PREFIX}-drawerHeader`,
    drawerContainer: `${PREFIX}-drawerContainer`,
    drawerTitle: `${PREFIX}-drawerTitle`,
};

const StyledDrawer = styled(Drawer)(({theme}) => ({
    [`& .${classes.drawer}`]: {
        width: drawerWidth,
        flexShrink: 0,
    },

    [`& .${classes.drawerPaper}`]: {
        width: drawerWidth,
    },

    [`& .${classes.drawerHeader}`]: {
        backgroundImage: "url('/drawer-background.png')",
        backgroundSize: drawerWidth,
        backgroundRepeat: "no-repeat",
        minHeight: 183,
    },

    [`& .${classes.drawerContainer}`]: {
        overflow: "auto",
    },

    [`& .${classes.drawerTitle}`]: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
}));

const drawerWidth = 340;

export const Menu = () => {
    const {menuOpen, museumUserOrAdmin} = useAppState();
    const {userData} = useAuthsState();
    const dispatch = useAppDispatch();
    const aboutDispatch = useAboutDispatch();
    const searchDispatch = useSearchDispatch();
    const t = useMenuTranslation();
    const isAppAdmin = useIsAppAdmin();
    const isMuseumAdmin = useIsAdminOfAtleastOneMuseum();

    useHasArcheologyModule();

    const {hasArcheologyModule} = useProjectState();

    const contributor = useMemo(() => userData['contributor'], [userData]);
    const externalUser = useMemo(
        () => !(userData['appAccess']?.museums.length > 0),
        [userData]
    );

    const navigate = useNavigate();

    const onMenuClick = (url) => {
        dispatch({type: MENU_CLOSED});
        if (url) {
            navigate(url);
        }
    };


    const getMyFolders = () => {
        if (!contributor && !externalUser) {
            return (
                <ListItemButton onClick={() => onMenuClick("/my-folders/")}>
                    <ListItemIcon>
                        <FolderIcon/>
                    </ListItemIcon>
                    <ListItemText primary={t("myFolders", "Mine mapper")}/>
                </ListItemButton>
            );
        }
    };

    const getAllFolders = () => {
        if (!contributor && !externalUser) {
            return (
                <ListItemButton onClick={() => onMenuClick("/all-folders/")}>
                    <ListItemIcon>
                        <FolderOpenIcon/>
                    </ListItemIcon>
                    <ListItemText primary={t("allFolders", "Alle mapper")}/>
                </ListItemButton>
            );
        }
    };

    const folders = (
        <List
            subheader={
                <ListSubheader component={"div"} id={"folders-subheader"}>
                    {t("folders", "Mapper")}
                </ListSubheader>
            }
        >
            {!externalUser && !contributor && getMyFolders()}
            <ListItemButton onClick={() => onMenuClick("/shared-folders/")}>
                <ListItemIcon>
                    <FolderSharedIcon/>
                </ListItemIcon>
                <ListItemText primary={t("sharedFolders", "Delte mapper")}/>
            </ListItemButton>
            {!externalUser && !contributor && getAllFolders()}
        </List>
    );

    const files = (
        <List
            subheader={
                <ListSubheader component={"div"} id={"folders-subheader"}>
                    {t("files", "Filer")}
                </ListSubheader>
            }
        >
            {!externalUser && (
                <ListItemButton onClick={() => {
                    searchDispatch({type: CLEAR_FQ});
                    searchDispatch({type: SET_ONLY_MINE});
                    onMenuClick("/my-files/");
                }}>
                    <ListItemIcon>
                        <InsertDriveFileIcon/>
                    </ListItemIcon>
                    <ListItemText primary={t("myFiles", "Mine filer")}/>
                </ListItemButton>
            )}
        </List>
    );

    const projects = (
        <List
            subheader={
                <ListSubheader component={"div"} id={"projects-subheader"}>
                    {t('menuItemModules', 'Moduler')}
                </ListSubheader>
            }
        >
            <ListItemButton
                onClick={() => {
                    onMenuClick("/all-projects/");
                }}
            >
                <ListItemIcon>
                    <AccountTreeIcon/>
                </ListItemIcon>
                <ListItemText primary={t('menuItemProjects', 'Prosjekter')}/>
            </ListItemButton>
        </List>
    );

    const appAdminListItems = () => {
        if (isAppAdmin) {
            return (<>
                    <ListItemButton onClick={() => onMenuClick('/add-to-index')}>
                        <ListItemIcon>
                            <DatasetIcon/>
                        </ListItemIcon>
                        <ListItemText primary={t('addToIndex', 'Legg til i indeks')}/>
                    </ListItemButton>
                    <ListItemButton onClick={() => onMenuClick("/reindex")}>
                        <ListItemIcon>
                            <RestoreIcon/>
                        </ListItemIcon>
                        <ListItemText primary={t('reindex', 'Reindex')}/>
                    </ListItemButton>
                    <ListItemButton onClick={() => onMenuClick('/import')}>
                        <ListItemIcon>
                            <ImportExportIcon/>
                        </ListItemIcon>
                        <ListItemText primary={t('import', 'Import')}/>
                    </ListItemButton>
                    <ListItemButton onClick={() => onMenuClick('/restore')}>
                        <ListItemIcon>
                            <RestoreFromTrashIcon/>
                        </ListItemIcon>
                        <ListItemText primary={t('restore', 'Gjenopprett')}/>
                    </ListItemButton>
                    <ListItemButton onClick={() => onMenuClick('/nb-publish-log')}>
                        <ListItemIcon>
                            <ReceiptLongIcon/>
                        </ListItemIcon>
                        <ListItemText primary={t('nbPublishing', 'NB Publisering')}/>
                    </ListItemButton>
                    <ListItemButton onClick={() => onMenuClick('/object-health')}>
                        <ListItemIcon>
                            <MonitorHeartIcon/>
                        </ListItemIcon>
                        <ListItemText primary={t('objectHealth', 'Objekthelse')}/>
                    </ListItemButton>
                    <ListItemButton onClick={() => onMenuClick('/regeneratemetadata')}>
                        <ListItemIcon>
                            <DataObjectIcon/>
                        </ListItemIcon>
                        <ListItemText primary={t('regeneratemetadata', 'Regenerer Metadata')}/>
                    </ListItemButton>
                </>
            );
        }
    };

    const museumAdminListItems = () => {
        if (isMuseumAdmin || isAppAdmin) {
            return (
                <>
                    <ListItemButton onClick={() => onMenuClick('/admin-local-assets')}>
                        <ListItemIcon>
                            <ListIcon/>
                        </ListItemIcon>
                        <ListItemText primary={t('adminLocalAssets', 'Lokale data')}/>
                    </ListItemButton>
                    <ListItemButton onClick={() => onMenuClick("/metadata-mapping")}>
                        <ListItemIcon>
                            <SyncAltIcon/>
                        </ListItemIcon>
                        <ListItemText primary={t('metadataMapping', 'Metadata mapping')}/>
                    </ListItemButton>
                </>
            );
        }
    };

    const adminMenu = () => {
        if (isAppAdmin || isMuseumAdmin) {
            return (
                <List
                    subheader={
                        <ListSubheader component="div" id="shared-folders-subheader">
                            {t('admin', 'Admin')}
                        </ListSubheader>
                    }
                >
                    {appAdminListItems()}
                    {museumAdminListItems()}

                    <ListItemButton onClick={() => onMenuClick('/stats/')}>
                        <ListItemIcon>
                            <AssessmentIcon/>
                        </ListItemIcon>
                        <ListItemText primary={t('statistics', 'Statistikk')}/>
                    </ListItemButton>

                </List>
            );
        }
    };

    return (museumUserOrAdmin &&
        <StyledDrawer
            open={menuOpen}
            onClose={() => dispatch({type: MENU_CLOSED})}
            slotProps={{
                paper: {
                    elevation: 4,
                    className: classes.drawerPaper,
                },
            }}
        >
            <div className={classes.drawerHeader}/>
            <div className={classes.drawerContainer}>
                <ListItemButton onClick={() => {
                    searchDispatch({type: CLEAR_FQ});
                    searchDispatch({type: CLEAR_ONLY_MINE});
                    onMenuClick("/");
                }}>
                    <ListItemIcon>
                        <HomeIcon/>
                    </ListItemIcon>
                    <ListItemText primary={t('home', 'Hjem')}/>
                </ListItemButton>
                <ListItemButton onClick={() => {
                    aboutDispatch({type: OPEN_ABOUT_DIALOG});
                    onMenuClick();
                }}>
                    <ListItemIcon>
                        <InfoIcon/>
                    </ListItemIcon>
                    <ListItemText primary={t('about', 'Om DAMS')}/>
                </ListItemButton>
                <InstallAppMenuItem/>
                <Divider/>
                {!externalUser && folders}
                {!externalUser && files}
                {!externalUser && hasArcheologyModule && projects}
                {adminMenu()}
            </div>
        </StyledDrawer>
    );
};
